// src/pages/SetupProgramPage.js
import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import apiClient from '../api';
import '../styles/SetupProgramPage.css';

const SetupProgramPage = ({ isAuthenticated }) => {
  const [programName, setProgramName] = useState('');
  const [programShortName, setProgramShortName] = useState('');
  const [assetType, setAssetType] = useState('points_off_chain');
  const [logoFile, setLogoFile] = useState(null); // State for the File object
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [programDescription, setProgramDescription] = useState('');
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;
    apiClient.get('/admin/subscription/status')
      .then((r) => setSubscription(r.data))
      .catch(() => setSubscription(null));
  }, [isAuthenticated]);

  useEffect(() => {
    // Cleanup for logo preview URL
    const currentPreviewUrl = logoPreviewUrl;
    return () => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const isPaid = subscription?.status === 'active';
  const isDemo = subscription?.status === 'demo';

  if (!isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setLogoFile(file); // Store the File object
      const previewUrl = URL.createObjectURL(file);
      setLogoPreviewUrl(previewUrl);
      setFormError('');
    } else {
      setLogoFile(null);
      setLogoPreviewUrl('');
      if (file) {
        setFormError('Please select a valid image file (PNG, JPG, SVG, GIF).');
      }
    }
  };

  const handleShortNameChange = (event) => {
      let value = event.target.value;
      value = value.toLowerCase().replace(/\s+/g, '');
      value = value.replace(/[^a-z0-9-]/g, '');
      if (value.length <= 10) { // Max 10 chars
          setProgramShortName(value);
      }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    // Client-side validation (same as before)
    if (!programName.trim()) { setFormError('Program Name is required.'); setIsSubmitting(false); return; }
    if (!programShortName.trim()) { setFormError('Short Name is required.'); setIsSubmitting(false); return; }
    if (programShortName.length > 10) { setFormError('Short Name cannot exceed 10 characters.'); setIsSubmitting(false); return; }
    if (!/^[a-z0-9-]+$/.test(programShortName)) { setFormError('Short Name can only contain lowercase letters, numbers, and hyphens.'); setIsSubmitting(false); return; }
    if (programDescription.length > 500) { setFormError('Program Description cannot exceed 500 characters.'); setIsSubmitting(false); return; }

    // --- UPDATED: Use FormData for multipart/form-data request ---
    const formData = new FormData();
    formData.append('name', programName.trim());
    formData.append('short_name', programShortName.trim());
    formData.append('asset_type', assetType);
    if (programDescription.trim()) { // Only append if not empty
        formData.append('description', programDescription.trim());
    }
    if (logoFile) {
        formData.append('logo_file', logoFile, logoFile.name); // Append the file object
    }
    // -------------------------------------------------------------

    console.log('Submitting Program Data via FormData...');
    // For debugging FormData content (cannot directly log formData object)
    // for (let [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }

    try {
      // --- UPDATED: Send formData. Axios will set Content-Type to multipart/form-data ---
      const response = await apiClient.post('/programs', formData, {
        headers: {
          // Axios typically sets this automatically for FormData,
          // but explicitly setting it can sometimes help if issues arise.
          // 'Content-Type': 'multipart/form-data', // Usually not needed
        }
      });
      // ----------------------------------------------------------------------------------

      console.log('Program Creation Response:', response.data);
      setSuccessMessage(`Program "${response.data.name}" created successfully! Short name: ${response.data.short_name}. Logo URL: ${response.data.logo_url || 'Not uploaded'}`);
      setProgramName('');
      setProgramShortName('');
      setProgramDescription('');
      setLogoFile(null);
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
      setLogoPreviewUrl('');
      // Clear file input visually (though state is already null)
      const fileInput = document.getElementById('logoUpload');
      if (fileInput) fileInput.value = null;

      setTimeout(() => {
         navigate('/select-program');
      }, 2000); // Increased delay slightly for success message
    } catch (err) {
      console.error("Program Creation Failed:", err.response || err.message || err);
      // Try to get more detailed error from FastAPI's validation if possible
      let detailedError = 'An unexpected error occurred.';
      if (err.response && err.response.data) {
          if (err.response.data.detail && typeof err.response.data.detail === 'string') {
              detailedError = err.response.data.detail;
          } else if (err.response.data.detail && Array.isArray(err.response.data.detail)) {
              // Handle FastAPI validation errors which are arrays of objects
              detailedError = err.response.data.detail.map(e => `${e.loc ? e.loc.join(' -> ') + ': ' : ''}${e.msg}`).join('; ');
          } else if (typeof err.response.data === 'string') {
              detailedError = err.response.data;
          }
      } else if (err.message) {
          detailedError = err.message;
      }
      setFormError(`Error creating program: ${detailedError}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="setup-program-container">
      <h2>Setup New Loyalty Program</h2>
      <p>Define the details for your new program.</p>

      {isDemo && (
        <div className="demo-banner">
          <strong>Demo mode</strong> — up to {subscription.user_cap} users, off-chain points only.
          Demo ends in {subscription.days_remaining} day(s); programs are deleted on expiry unless
          you subscribe.
        </div>
      )}

      <form onSubmit={handleSubmit} className="setup-program-form">
        {formError && <div className="form-error-message">{formError}</div>}
        {successMessage && <div className="form-success-message">{successMessage}</div>}

        <div className="form-group">
          <label htmlFor="programName">Program Name *</label>
          <input
            type="text" id="programName" name="programName"
            placeholder="e.g., My Awesome Coffee Rewards" required
            value={programName} onChange={(e) => setProgramName(e.target.value)}
            disabled={isSubmitting} maxLength={100}
          />
          <small>The full name of your loyalty program (unique per user).</small>
        </div>

        <div className="form-group">
            <label htmlFor="programShortName">Short Name * (max 10 chars)</label>
            <input
                type="text" id="programShortName" name="programShortName"
                placeholder="e.g., coffee-1" required
                value={programShortName} onChange={handleShortNameChange}
                disabled={isSubmitting}
                maxLength={10}
                //pattern="^[a-z0-9-]+$"
                title="Max 10 chars. Lowercase letters, numbers, hyphens only. No spaces."
            />
            <small>A unique, short identifier (max 10 chars, lowercase, numbers, hyphens only).</small>
        </div>

        <div className="form-group">
          <label htmlFor="programDescription">Program Description (Optional)</label>
          <textarea
            id="programDescription" name="programDescription"
            placeholder="Briefly describe your loyalty program (e.g., for the user app landing page)."
            value={programDescription}
            onChange={(e) => setProgramDescription(e.target.value)}
            disabled={isSubmitting}
            rows={3}
            maxLength={500}
          />
          <small>A short description for your program (max 500 chars).</small>
        </div>

        <div className="form-group">
          <label htmlFor="assetType">Asset Type</label>
          <select
            id="assetType"
            name="assetType"
            required
            value={assetType}
            onChange={(e) => setAssetType(e.target.value)}
            disabled={isSubmitting}
          >
            <option value="points_off_chain">Points (Off-Chain)</option>
            <option value="points_on_chain" disabled={!isPaid}>
              ASA / On-Chain {isPaid ? '' : '(requires paid subscription)'}
            </option>
          </select>
          <small>
            {isPaid
              ? 'On-chain ASA distributes a real token. You can mint your own ASA after creating the program, or use the gramo token by default.'
              : 'On-chain ASA is unlocked with a paid subscription.'}
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="logoUpload">Program Logo (Optional)</label>
          <input
            type="file" id="logoUpload" name="logoUpload" // Name attribute is good practice
            accept="image/png, image/jpeg, image/gif, image/svg+xml"
            onChange={handleLogoChange} disabled={isSubmitting}
          />
           <small>Recommended: Square image. Max 2MB.</small>
           {logoPreviewUrl && (
             <div className="logo-preview-container">
                <p>Logo Preview:</p>
                <img src={logoPreviewUrl} alt="Logo Preview" className="logo-preview-image" />
             </div>
           )}
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={isSubmitting} >
            {isSubmitting ? 'Creating...' : 'Create Program'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SetupProgramPage;
